import { GetServiceV2Dto, GetTransactionDto } from "@api/api";
import { ExtendedService } from "@core/model/extended-service.interface";
import { ServiceEnum } from "@core/model/service-enum";
import { useTransactionUtil } from "@core/utility/transaction.helper";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { isBefore } from "date-fns";
import { flatMap } from "lodash-es";
import { useCallback, useState } from "react";

export function useTransactionHelper() {
  const [timeLeft, setTimeLeft] = useState<Record<string, string>>({});
  const { getServiceStart, getEndDateTime } = useTransactionUtil();
  const { getDateTimeLocal, formatDateLocal } = useDateTimeContext();

  const combinedServices = useCallback((services: GetServiceV2Dto[]) => {
    const filteredService = services.filter((item) => !!item);
    const combinedMembersMap = filteredService.reduce<Map<string, GetServiceV2Dto>>((acc, item) => {
      if (item.member && item.is_open_time) {
        acc.set(item.member, { ...item });
        return acc;
      }

      if (!item.member) {
        return acc;
      }

      if (!acc.has(item.member)) {
        acc.set(item.member, { ...item });
        return acc;
      }

      const currentItem = acc.get(item.member)!;
      const currentStart = getServiceStart(currentItem);
      const newItemStart = getServiceStart(item);

      if (newItemStart.getTime() < currentStart.getTime()) {
        currentItem.date = item.date;
        currentItem.time = item.time;
        currentItem.created_at = item.created_at;
      }

      const currentDuration = currentItem.duration ?? 0;
      const currentAmount = currentItem.amount ?? 0;
      const currentServiceName = currentItem.service_name;

      currentItem.duration = currentDuration + (item.duration ?? 0);
      currentItem.amount = currentAmount + (item.amount ?? 0);
      currentItem.service_name = `${currentServiceName} (MULTIPLE SERVICE)`;

      acc.set(item.member, currentItem);
      return acc;
    }, new Map<string, GetServiceV2Dto>());

    return Array.from(combinedMembersMap.values());
  }, [getServiceStart]);

  const expiryTimer = useCallback(
    (transactionData: GetTransactionDto[]) => {
      const now = new Date();

      const services = flatMap(
        transactionData.map((item) => item.service)
      ) as GetServiceV2Dto[];

      const todayString = formatDateLocal(now);

      // Filter out individually expired (stale) services before combining.
      // This prevents old un-checked-out ongoing stays from previous days
      // from being merged with today's new bookings and incorrectly showing "Expired".
      // We keep today's expired stays so they correctly show "Expired".
      const nonExpiredServices = services.filter((s) => {
        if (!s || s.is_open_time || s.is_checkout) return true; // always keep open-time and checked-out services
        const sEnd = getEndDateTime(s);
        const isToday = s.date === todayString;
        return isToday || sEnd.getTime() > now.getTime(); // keep if it's from today OR if it hasn't expired yet
      });

      // Find services that were filtered out because they are stale expired stays from previous days
      const expiredStaleServices = services.filter((s) => {
        if (!s || s.is_open_time || s.is_checkout) return false;
        const sEnd = getEndDateTime(s);
        const isToday = s.date === todayString;
        return !isToday && sEnd.getTime() <= now.getTime();
      });

      const getFormattedDuration = (service: GetServiceV2Dto) => {
        const durationVal = service.duration || 0;
        const hrs = Math.floor(durationVal / 60);
        const mins = durationVal % 60;
        return hrs > 0 
          ? (mins > 0 ? `${hrs}hr ${mins}m` : `${hrs}hr`)
          : `${mins}m`;
      };

      // Initialize timeLeft mapping with duration for these stale services
      const initialTimeLeft = expiredStaleServices.reduce((acc, service) => {
        const formatted = getFormattedDuration(service);
        acc[service.code] = formatted;
        if (service.member) acc[service.member] = formatted;
        return acc;
      }, {} as Record<string, string>);

      const groupedMembers = combinedServices(nonExpiredServices);
      const updatedTimeLeft = groupedMembers.reduce((acc, service) => {
        const serviceStart = getServiceStart(service);
        const isTodayAfterServiceStart = isBefore(now, serviceStart);

        if (isTodayAfterServiceStart) {
          acc[service.code] = "N/A";
          if (service.member) acc[service.member] = "N/A";
        } else if (service.is_open_time) {
          const serviceStart = getServiceStart(service);

          const diffInStock = now.getTime() - serviceStart.getTime();
          const hoursInStock = Math.floor(diffInStock / (1000 * 60 * 60));
          const minutesInStock = Math.floor(
            (diffInStock % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((diffInStock % (1000 * 60)) / 1000);

          const timeInStock =
            hoursInStock > 0
              ? `${hoursInStock}hr ${minutesInStock}m ${seconds}s since`
              : `${minutesInStock}m ${seconds}s since`;
          acc[service.code] = timeInStock;
          if (service.member) acc[service.member] = timeInStock;
        } else if (service.is_checkout) {
          acc[service.code] = ServiceEnum.Checkout;
          if (service.member) acc[service.member] = ServiceEnum.Checkout;
        } else {
          const expiryDate = getEndDateTime(service);
          const diff = expiryDate.getTime() - now.getTime();

          if (diff <= 0) {
            const formatted = getFormattedDuration(service);
            acc[service.code] = formatted;
            if (service.member) acc[service.member] = formatted;
          } else {
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            let expiryText = "";
            if (hours > 0) {
              expiryText = `${hours}hr ${minutes}m ${seconds}s left`;
            } else if (minutes > 0) {
              expiryText = `${minutes}m ${seconds}s left`;
            } else {
              expiryText = `${seconds}s left`;
            }
            acc[service.code] = expiryText;
            if (service.member) acc[service.member] = expiryText;
          }
        }

        return acc;
      }, initialTimeLeft);

      return updatedTimeLeft;
    },
    [combinedServices, getDateTimeLocal, getEndDateTime, getServiceStart]
  );

  const expandedService = useCallback((item: GetTransactionDto) => {
    const services = item.service || [];
    const extendedServices = services.map(
      (serviceItem) =>
        ({
          ...serviceItem,
          status: item.status,
          transactionCode: item.code,
          is_paid: item.is_paid,
          parent_first_name: item.parent_first_name,
          parent_last_name: item.parent_last_name,
          mobile_number: item.mobile_number,
        } as ExtendedService)
    );
    return extendedServices;
  }, []);

  return {
    timeLeft,
    expandedService,
    setTimeLeft,
    combinedServices,
    expiryTimer,
  };
}

export default useTransactionHelper;
